import { useEffect, useMemo, useState } from "react";
import { api, buildViewerLoginPageUrl } from "../lib/api";
import { Search, Rabbit, LogOut, Sparkles, PauseCircle } from "lucide-react";
import { toast } from "sonner";
import { CurrencyIcon, currencyLabel, visibleCurrencies } from "../lib/currencyDisplay";

export default function Interact() {
  const [data, setData] = useState(null);
  const [me, setMe] = useState({ authenticated: false });
  const [search, setSearch] = useState("");
  const [inputValues, setInputValues] = useState({});

  const load = async () => {
    try {
      const [{ data: d }, { data: m }] = await Promise.all([
        api.get("/interactions"),
        api.get("/viewer/me"),
      ]);
      setData(d);
      setMe(m);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, []);

  const currencyMap = useMemo(() => {
    const m = {};
    (data?.currencies || []).forEach(c => { m[c.id] = c; });
    return m;
  }, [data]);
  const interactionCategoryMap = useMemo(() => {
    const m = {};
    (data?.interaction_categories || []).forEach(c => { m[c.slug] = c; });
    return m;
  }, [data]);
  const publicDefinitions = useMemo(() => {
    if (!data) return [];
    let items = data.interaction_definitions || [];
    const q = search.trim().toLowerCase();
    if (q) items = items.filter(i => {
      const categoryNames = (i.category_tags || [])
        .map((slug) => interactionCategoryMap[slug]?.name || slug)
        .join(" ");
      return (
        i.name.toLowerCase().includes(q) ||
        (i.description || "").toLowerCase().includes(q) ||
        categoryNames.toLowerCase().includes(q) ||
        (i.cost_type || "").toLowerCase().includes(q) ||
        viewerCurrencyName(currencyMap[i.cost_type] || { id: i.cost_type }).toLowerCase().includes(q)
      );
    });
    return items;
  }, [currencyMap, data, interactionCategoryMap, search]);

  const visibleCurrencyList = useMemo(() => visibleCurrencies(data?.currencies || []), [data]);

  const logout = async () => { await api.post("/viewer/logout").catch(() => {}); await load(); };

  const redeem = async (item) => {
    if (data?.interactions_paused) {
      toast.error("Interactions are paused.");
      return;
    }
    if (!me?.authenticated) {
      toast.error("Sign in to redeem.");
      return;
    }
    const viewerInput = String(inputValues[item.id] || "").trim();
    const viewerInputConfig = interactionViewerInputConfig(item);
    if (viewerInputConfig.required && !viewerInput) {
      toast.error(`Add ${viewerInputConfig.label.toLowerCase()}.`);
      return;
    }
    try {
      await api.post("/viewer/redeem-requests", {
        interaction_id: item.id,
        input_values: {
          viewer_input: viewerInput,
          requester_display_name: me.viewer?.display_name || me.viewer?.login || "Viewer",
          requester_login: me.viewer?.login || "",
        },
      });
      toast.success(`Redeem request created for "${item.name}"`);
      setInputValues((prev) => ({ ...prev, [item.id]: "" }));
      await load();
    } catch (err) {
      toast.error(readErrorDetail(err) || "Redeem request failed");
    }
  };

  if (!data) return <div className="p-10 text-sm">Loading Viewer Redeems...</div>;

  return (
    <div className="min-h-screen grid-bg bg-noise">
      <div className="relative max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded bg-[hsl(var(--primary))] flex items-center justify-center">
              <Rabbit className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-lg font-semibold tracking-tight">TheBarracksBunny AI</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">Viewer Redeems  -  {data.streamer?.display_name && `live for ${data.streamer.display_name}`}</div>
            </div>
          </div>

          {me?.authenticated ? (
            <div className="flex items-center gap-3" data-testid="viewer-identity">
              {me.viewer.profile_image_url && <img src={me.viewer.profile_image_url} alt="" className="w-8 h-8 rounded-full" />}
              <div className="text-sm">
                <div>{me.viewer.display_name}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] flex gap-3 font-mono">
                  {visibleCurrencyList.map(c => (
                    <span key={c.id} style={{ color: c.color }}>{me.viewer.balances?.[c.id] ?? 0} {viewerCurrencyName(c)}</span>
                  ))}
                </div>
              </div>
              <button onClick={logout} className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] hover:text-white flex items-center gap-1" data-testid="viewer-logout"><LogOut className="w-3 h-3" /> log out</button>
            </div>
          ) : (
            <a
              href={buildViewerLoginPageUrl("/interact")}
              target="_top"
              rel="noopener"
              data-testid="viewer-login-btn"
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded bg-[hsl(var(--primary))] text-white hover:brightness-110"
            >
              <Rabbit className="w-4 h-4" /> Sign in to interact
            </a>
          )}
        </div>

        <div>
          <div className="label-eyebrow">Viewer Redeems</div>
          <h1 className="text-4xl font-bold tracking-tight mt-1">Choose your interaction.</h1>
          <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">Pick from the currently available public redeems.</p>
        </div>

        {data?.interactions_paused && (
          <div className="mt-5 rounded border border-amber-400/45 bg-amber-400/10 px-4 py-3 flex items-center gap-2 text-amber-100">
            <PauseCircle className="w-4 h-4" />
            <span className="text-sm font-semibold">Interactions Paused</span>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="surface flex items-center gap-2 px-3 py-2 flex-1 min-w-[260px] max-w-md">
            <Search className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search interactions..." data-testid="interact-search" className="bg-transparent flex-1 text-sm focus:outline-none" />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="public-interactions-grid">
          {publicDefinitions.length === 0 && (
            <div className="surface col-span-full p-8 sm:p-10 text-center">
              <div className="mx-auto mb-4 h-11 w-11 rounded bg-[hsl(var(--surface-2))] flex items-center justify-center border border-[hsl(var(--border))]">
                <Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" />
              </div>
              <div className="text-lg font-semibold text-white">
                {search.trim() ? "No redeems matched your search." : "No public redeems are available yet."}
              </div>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
                {search.trim() ? "Try a different name, description, category, or token type." : "Check back soon for viewer interactions you can redeem."}
              </p>
            </div>
          )}
          {publicDefinitions.map(item => {
            const accentColor = item.accent_color || "#0A84FF";
            const currency = currencyMap[item.cost_type];
            const tags = (item.category_tags || []).map((slug) => interactionCategoryMap[slug] || { slug, name: slug, color: "hsl(var(--muted-foreground))" });
            const costLabel = viewerCurrencyName(currency || { id: item.cost_type });
            const viewerInput = interactionViewerInputConfig(item);
            return (
              <div
                key={item.id}
                className="surface p-4 sm:p-5 flex flex-col gap-4 min-h-[220px]"
                data-testid={`public-interaction-${item.id}`}
                style={{ borderLeft: `3px solid ${accentColor}`, boxShadow: `inset 0 1px 0 ${accentColor}22` }}
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded bg-[hsl(var(--surface-2))] flex items-center justify-center shrink-0" style={{ border: `1px solid ${accentColor}66` }}>
                    <CurrencyIcon currency={{ icon: item.icon, color: accentColor }} className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold leading-snug text-[15px] line-clamp-2 [overflow-wrap:anywhere]" title={item.name}>{item.name}</div>
                    {item.description && <div className="text-sm leading-relaxed text-[hsl(var(--muted-foreground))] mt-1.5 line-clamp-3">{item.description}</div>}
                  </div>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map((tag) => (
                      <span key={tag.slug} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border bg-[hsl(var(--surface-2))]/60" style={{ color: tag.color, borderColor: `${tag.color}66` }}>
                        {tag.name}
                      </span>
                    ))}
                  </div>
                )}
                {viewerInput.required && (
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]" htmlFor={`viewer-input-${item.id}`}>
                      {viewerInput.label}
                    </label>
                    <textarea
                      id={`viewer-input-${item.id}`}
                      value={inputValues[item.id] || ""}
                      maxLength={viewerInput.maxLength}
                      onChange={(event) => setInputValues((prev) => ({ ...prev, [item.id]: event.target.value }))}
                      placeholder={viewerInput.placeholder}
                      className="w-full min-h-[76px] rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] px-3 py-2 text-sm text-white placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:border-[hsl(var(--primary))]"
                      data-testid={`interact-viewer-input-${item.id}`}
                    />
                  </div>
                )}
                <div className="mt-auto pt-3 border-t border-[hsl(var(--border))] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="uppercase tracking-widest text-[10px] text-[hsl(var(--muted-foreground))]">Cost</div>
                    <div className="mt-1 flex items-center gap-2 min-w-0">
                      <CurrencyIcon currency={currency || { id: item.cost_type }} className="w-4 h-4 shrink-0" />
                      <span className="font-mono text-sm text-white shrink-0">{item.cost_amount}</span>
                      <span className="text-sm text-[hsl(var(--muted-foreground))] truncate">{costLabel}</span>
                    </div>
                  </div>
                  {me?.authenticated ? (
                    <button
                      onClick={() => redeem(item)}
                      disabled={data?.interactions_paused}
                      className="text-xs uppercase tracking-widest px-4 py-2 rounded text-white shrink-0"
                      style={{ background: data?.interactions_paused ? "rgba(0,0,0,0.5)" : accentColor }}
                      data-testid={`redeem-interaction-${item.id}`}
                    >
                      {data?.interactions_paused ? "Paused" : "Redeem"}
                    </button>
                  ) : (
                    <a href={buildViewerLoginPageUrl("/interact")} target="_top" rel="noopener" className="text-xs uppercase tracking-widest px-4 py-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white hover:border-[hsl(var(--primary))] shrink-0 text-center">Sign in</a>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-xs text-[hsl(var(--muted-foreground))] text-center">
          View top earners on the <a href="/leaderboard" className="underline text-[hsl(var(--primary))]">leaderboard</a>  -  profile at <code className="font-mono text-white">!profile</code>.
        </div>
      </div>
    </div>
  );
}

function readErrorDetail(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (detail?.error) return detail.error;
  return "";
}

function interactionViewerInputConfig(interaction) {
  const fallback = {
    required: false,
    label: "Viewer input",
    placeholder: "Type your message",
    maxLength: 280,
  };
  for (const action of interaction.actions || []) {
    const config = action.config || {};
    if (action.action_type === "tts_generate_audio" && (
      config.text_mode === "user_input"
      || config.input_text_key === "viewer_input"
      || config.input_text_key === "user_input"
      || config.input_text_key === "requester_input"
    )) {
      return {
        required: true,
        label: "Message for TTS",
        placeholder: "Type what Bunny should say",
        maxLength: Number(config.max_character_length || 280),
      };
    }
    if (action.action_type === "tts" && (
      config.text_source === "requester_input"
      || config.text_source === "viewer_input"
      || config.text_source === "user_input"
    )) {
      return {
        required: true,
        label: "Message for TTS",
        placeholder: "Type what Bunny should say",
        maxLength: Number(config.max_character_length || 280),
      };
    }
    if (action.action_type === "ai_generate_text" && (
      config.requires_viewer_input === true
      || config.prompt_mode === "viewer_topic"
      || promptUsesViewerInput(config.prompt_template)
    )) {
      const label = String(config.viewer_input_label || "Topic").trim() || "Topic";
      return {
        required: true,
        label,
        placeholder: label.toLowerCase() === "topic" ? "Type a topic, like haunted cupcakes" : `Type ${label.toLowerCase()}`,
        maxLength: Number(config.viewer_input_max_length || 280),
      };
    }
  }
  return fallback;
}

function promptUsesViewerInput(promptTemplate) {
  const prompt = String(promptTemplate || "");
  return /\{\{?\s*(viewer_input|user_input|requester_input|topic)\s*\}?\}/i.test(prompt);
}

function viewerCurrencyName(currency) {
  const fallbackNames = {
    free_currency: "Bunny Tokens",
    paid_currency: "Rat Tokens",
    giveaway_currency: "Giveaway Tokens",
    event_currency: "Event Tokens",
  };
  return fallbackNames[currency?.id] || currencyLabel(currency);
}
